import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NavigationBarComponent } from '../navigation-bar/navigation-bar.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';

@Component({
  selector: 'app-mortgage-pmt',
  standalone: true,
  imports: [RouterModule, NavigationBarComponent, FormsModule, CommonModule, HighchartsChartModule],
  templateUrl: './mortgage-pmt.component.html',
  styleUrls: ['./mortgage-pmt.component.css']
})
export class MortgagePmtComponent implements OnInit {
  loanAmountStr: string = '';
  interestRateStr: string = '';
  durationYearsStr: string = '';
  paymentFrequency: string = 'monthly';
  mortgageSchedule: any[] = [];
  activeTab: 'schedule' | 'chart' = 'schedule';

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};

  constructor(private titleService: Title, private metaService: Meta) {}

  ngOnInit(): void {
    const title = 'Mortgage Payment Calculator - Calculate Your Monthly Payment';
    const description = 'Use our Mortgage Payment Calculator to easily calculate your monthly mortgage payments. Enter your loan amount, interest rate, and duration to get an amortization schedule and payment breakdown.';

    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'description', content: description });
  }

  onSubmit(): void {
    const loanAmount = Number(this.loanAmountStr);
    const annualRate = Number(this.interestRateStr);
    const durationYears = Number(this.durationYearsStr);
  
    const maxInterestRate = 30;
    if (
      isNaN(loanAmount) || loanAmount <= 0 ||
      isNaN(annualRate) || annualRate <= 0 || annualRate > maxInterestRate ||
      isNaN(durationYears) || durationYears <= 0
    ) {
      alert(`Please enter valid values. Interest rate must be > 0 and ≤ ${maxInterestRate}%.`);
      return;
    }
  
    const paymentsPerYear = this.paymentFrequency === 'monthly' ? 12 :
                            this.paymentFrequency === 'quarterly' ? 4 : 1;
    const totalPayments = durationYears * paymentsPerYear;
    const periodicRate = (annualRate / 100) / paymentsPerYear;
  
    const paymentAmount = (loanAmount * periodicRate) /
      (1 - Math.pow(1 + periodicRate, -totalPayments));
  
    const scheduleTable: any[] = [];
    const headers = ['Payment #', 'Payment Date', 'Payment Amount', 'Principal', 'Interest', 'Remaining Loan'];
  
    let balance = loanAmount;
    let paymentNumber = 1;
    let currentDate = new Date();
    currentDate.setDate(1);
  
    const chartData = {
      cumulativeInterest: [] as number[],
      cumulativePrincipal: [] as number[],
      balanceOverTime: [] as number[],
      labels: [] as string[]
    };
  
    let totalInterest = 0;
    let totalPrincipal = 0;
  
    while (balance > 0 && paymentNumber <= totalPayments) {
      const interest = balance * periodicRate;
      const principal = paymentAmount - interest;
      const remainingLoan = balance - principal;
  
      scheduleTable.push([
        paymentNumber,
        currentDate.toDateString(),
        paymentAmount.toFixed(2),
        principal.toFixed(2),
        interest.toFixed(2),
        remainingLoan.toFixed(2)
      ]);
  
      totalInterest += interest;
      totalPrincipal += principal;
  
      if (paymentNumber % paymentsPerYear === 0 || paymentNumber === totalPayments) {
        const yearLabel = `${Math.floor(paymentNumber / paymentsPerYear)} yr`;
        chartData.labels.push(yearLabel);
        chartData.cumulativeInterest.push(+totalInterest.toFixed(2));
        chartData.cumulativePrincipal.push(+totalPrincipal.toFixed(2));
        chartData.balanceOverTime.push(+remainingLoan.toFixed(2));
      }
  
      balance = remainingLoan;
      paymentNumber++;
  
      if (this.paymentFrequency === 'monthly') {
        currentDate.setMonth(currentDate.getMonth() + 1);
      } else if (this.paymentFrequency === 'quarterly') {
        currentDate.setMonth(currentDate.getMonth() + 3);
      } else if (this.paymentFrequency === 'annually') {
        currentDate.setFullYear(currentDate.getFullYear() + 1);
      }
    }
  
    this.mortgageSchedule = [headers, ...scheduleTable];
  
    // Line chart for principal, interest, and balance over time
    this.chartOptions = {
      chart: { type: 'line' },
      title: { text: 'Mortgage Breakdown Over Time' },
      xAxis: {
        categories: chartData.labels,
        title: { text: 'Years' }
      },
      yAxis: {
        title: { text: 'Dollars ($)' },
        min: 0
      },
      tooltip: { shared: true },
      series: [
        {
          name: 'Cumulative Interest Paid',
          type: 'line',
          data: chartData.cumulativeInterest,
          color: '#f44336'
        },
        {
          name: 'Cumulative Principal Paid',
          type: 'line',
          data: chartData.cumulativePrincipal,
          color: '#4caf50'
        },
        {
          name: 'Remaining Loan Balance',
          type: 'line',
          data: chartData.balanceOverTime,
          color: '#2196f3'
        }
      ]
    };
  }  
}
